import { ActionIcon, Tooltip } from '@mantine/core'
import { TbLayoutSidebarLeftCollapseFilled, TbLayoutSidebarLeftExpandFilled } from 'react-icons/tb'
import { useNavigationCollapse } from '@/hooks/useNavigationCollapse'
import { useTranslation } from 'react-i18next'

interface NavigationToggleProps {
  /**
   * Size of the toggle button
   * @default 32
   */
  size?: number
  /**
   * Whether to show a tooltip
   * @default true
   */
  showTooltip?: boolean
  /**
   * Custom tooltip labels
   */
  tooltipLabels?: {
    collapse: string
    expand: string
  }
  storageKey?: string
  defaultCollapsed?: boolean
}

/**
 * A reusable navigation toggle button component that uses the navigation collapse hook
 */
export const NavigationToggle: React.FC<NavigationToggleProps> = ({
  size = 32,
  showTooltip = true,
  tooltipLabels,
  storageKey,
  defaultCollapsed = false,
}) => {
  const { collapsed, toggleCollapsed } = useNavigationCollapse({ storageKey, defaultCollapsed })
  const { t } = useTranslation('common')
  const resolvedTooltipLabels = tooltipLabels ?? {
    collapse: t('collapseNavigation'),
    expand: t('expandNavigation'),
  }

  const button = (
    <ActionIcon
      size={size}
      variant="default"
      aria-label={collapsed ? resolvedTooltipLabels.expand : resolvedTooltipLabels.collapse}
      onClick={toggleCollapsed}
    >
      {collapsed ? (
        <TbLayoutSidebarLeftExpandFilled
          size={size * 0.75}
          color={'light-dark(var(--mantine-color-gray-6), #c9c9c9)'}
        />
      ) : (
        <TbLayoutSidebarLeftCollapseFilled
          size={size * 0.75}
          color={'light-dark(var(--mantine-color-gray-6), #c9c9c9)'}
        />
      )}
    </ActionIcon>
  )

  if (showTooltip) {
    return (
      <Tooltip
        label={collapsed ? resolvedTooltipLabels.expand : resolvedTooltipLabels.collapse}
        arrowOffset={10}
        position="right"
        withArrow
      >
        {button}
      </Tooltip>
    )
  }

  return button
}

export default NavigationToggle
